import { type CSSProperties, useEffect, useRef, useState } from "react";
import Button from "../../../../../../components/atoms/button";
import ObjectiveOptionsInput from "./objectiveOptionsInput";
import GetHeroIcon from "../../../../../../utils/getHeroIcons";
import { useAppDispatch, useAppSelector } from "../../../../../../store/store";
import { AssessmentSliceActions } from "@assessment/store/slices/assessmentSlice";
import {
  useAttemptQuestionMutation,
  useFlagQuestionMutation,
  useLazyHeartBeatApiQuery,
  useStartQuestionMutation
} from "../../../../../../store/slices/api/assessmentApiSlice";
import { encodeToBase64 } from "@lxp-app/utils/encodeImperva.ts";
import ObjectiveFibQuestion from "./subtypes/objectiveFibQuestion";
import { Question } from "../../../../../../types/assessmentOngoing";
import { useShowErrors } from "../../../toasterWrapper";
import Tooltip from "@assessment/components/atoms/tooltip";
import ShowExplanation from "../../hint-explanation/explanationView";
import ShowHint from "../../hint-explanation/showHint";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

interface AssessmentObjectiveQuestions {
  question: Question;
  sectionId: string;
  totalQuestionsInSection: number;
  changeActiveQuestion: (val: "next" | "previous") => void;
  targetSection: any;
}

const AssessmentObjectiveQuestions = ({
  question,
  sectionId,
  totalQuestionsInSection,
  changeActiveQuestion,
  targetSection
}: AssessmentObjectiveQuestions) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { scheduleId } = useParams();
  const showErrors = useShowErrors();

  const [attemptQuestion] = useAttemptQuestionMutation();
  const [startQuestion] = useStartQuestionMutation();
  const [flagQuestion] = useFlagQuestionMutation();
  const [heartBeatApi] = useLazyHeartBeatApiQuery();

  const IS_FIB_QUESTION = question.subType === "fib";

  const ongoingId = useAppSelector((store) => store.AssessmentSlice.ongoingId);

  const constants = useAppSelector((store) => store.AssessmentSlice.duration);

  const activeQuestionMeta = useAppSelector(
    (store) => store.AssessmentSlice.activeQuestionMeta
  );

  const responses = useAppSelector(
    (store) => store.AssessmentSlice.candidateResponse
  );

  const responsesFib = useAppSelector(
    (store) => store.AssessmentSlice.candidateFibResponse
  );

  const assessmentProgress = useAppSelector(
    (state) => state.AssessmentSlice.assessmentProgress
  );

  const currentStatus =
    assessmentProgress[targetSection.sectionId].questions[question.questionId]
      .status;

  const isLastQuestionInTheSection =
    question.orderInSection === totalQuestionsInSection;

  const candidateResponseForQuestion: any = activeQuestionMeta?.questionId
    ? IS_FIB_QUESTION
      ? responsesFib[activeQuestionMeta.questionId!]
      : responses[activeQuestionMeta.questionId!]
    : undefined;

  const questionFlagged =
    assessmentProgress[sectionId].questions[question.questionId].flagged;

  const preAssessmentData = useAppSelector(
    (state) => state.AssessmentSlice.preAssessmentData
  );

  const showQuestionTitle = useAppSelector(
    (state) =>
      state.AssessmentSlice.preAssessmentData.questionConfig.showQuestionTitle
  );

  const [attemptingQuestion, setAttemptingQuestion] = useState(false);
  const [selectedOption, setSelectedOption] = useState<string[]>([]);
  const [showExplanation, setShowExplanation] = useState(false);
  const [revealedHintsCount, setRevealedHintsCount] = useState(0);
  const [isHintLoading, setIsHintLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const hints = question?.hint || [];
  const [hasSavedAnswer, setHasSavedAnswer] = useState(false);
  const [hasInteractedWithQuestion, setHasInteractedWithQuestion] =
    useState(false);
  const [showHintSection, setShowHintSection] = useState(false);
  const [hasSavedOnce, setHasSavedOnce] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [availableViewportHeight, setAvailableViewportHeight] = useState<
    number | null
  >(null);

  const hasSelectedAnswer = IS_FIB_QUESTION
    ? candidateResponseForQuestion &&
      Object.values(candidateResponseForQuestion).some(
        (val: any) => val?.trim() !== ""
      )
    : candidateResponseForQuestion && candidateResponseForQuestion.length > 0;

  const handleExplanationClick = () => {
    setShowExplanation((prev) => !prev);
  };

  useEffect(() => {
    if (IS_FIB_QUESTION) {
      setSelectedOption(Object.keys(candidateResponseForQuestion || {}));
    } else {
      setSelectedOption(candidateResponseForQuestion || []);
    }
  }, [question.questionId, candidateResponseForQuestion]);

  const updateAnswerKeyHandler = (newAnswerKey: string[] | []) => {
    setShowExplanation(false);
    setHasInteractedWithQuestion(true);
    dispatch(
      AssessmentSliceActions.mutateCandidateResponse({
        questionId: question.questionId,
        answerKey: newAnswerKey
      })
    );
  };

  useEffect(() => {
    setHasSavedOnce(false);
    setRevealedHintsCount(0);
    if (
      isExplanationPresent(question?.explanation) &&
      (IS_FIB_QUESTION
        ? Object.keys(candidateResponseForQuestion || [])?.length > 0
        : candidateResponseForQuestion &&
          candidateResponseForQuestion?.length > 0)
    ) {
      setShowExplanation(true);
    } else {
      setShowExplanation(false);
    }
    setShowHintSection(false);
    setIsHintLoading(false);
  }, [question.questionId]);

  const updateFibAnswerKeyHandler = (newAnswerKey: Record<string, string>) => {
    setShowExplanation(false);
    setHasInteractedWithQuestion(true);
    dispatch(
      AssessmentSliceActions.mutateCandidateFibResponse({
        questionId: question.questionId,
        answerKey: newAnswerKey
      })
    );
  };
  useEffect(() => {
    if (showExplanation) {
      const el = document.getElementById("explanation-section");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }, [showExplanation]);

  useEffect(() => {
    if (showHintSection) {
      const el = document.getElementById("hints-section");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }, [showHintSection]);

  useEffect(() => {
    if (revealedHintsCount > 0) {
      const index = revealedHintsCount - 1;
      const el = document.getElementById(`hint-${index}`);
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100); // small delay to ensure rendering
      }
    }
  }, [revealedHintsCount]);

  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     heartBeatApiCall();
  //   }, constants?.idle * 1000);

  //   return () => clearInterval(interval);
  // }, []);

  // const heartBeatApiCall = async () => {
  //   try {
  //     if (!scheduleId) {
  //       throw {
  //         status: "",
  //         title: "Schedule Id Not Found",
  //         err: {},
  //         description: ""
  //       };
  //     }
  //     if (!ongoingId) {
  //       throw {
  //         status: "",
  //         title: "Ongoing Id Not Found",
  //         err: {},
  //         description: ""
  //       };
  //     }
  //     const heartBeatRes = await heartBeatApi({
  //       ongoingId: ongoingId,
  //       scheduleId: scheduleId
  //     });
  //     if (heartBeatRes?.data?.data?.ongoingQPDuration) {
  //       dispatch(
  //         AssessmentSliceActions.mutateTimeLeft(
  //           heartBeatRes?.data?.data?.ongoingQPDuration
  //         )
  //       );
  //     }
  //   } catch (error: any) {
  //     console.log("error", error);
  //   }
  // };

  const attemptQuestionHandler = async (
    isFinalSubmit: boolean = false,
    revealExplanationOnly: boolean = false
  ) => {
    setAttemptingQuestion(true);
    try {
      if (isExplanationPresent(question?.explanation)) setShowExplanation(true);
      if (!ongoingId) {
        throw {
          status: "",
          title: "Ongoing Id Not Found",
          err: {},
          description: ""
        };
      }

      const newAnswer =
        candidateResponseForQuestion || (IS_FIB_QUESTION ? {} : []);
      const fibAnswer: Record<string, string> = {};
      if (IS_FIB_QUESTION && Object.keys(newAnswer).length > 0) {
        /* We are renaming all keys to replace blank with &blank  in case of FIB  solution*/

        for (const key in newAnswer) {
          if (newAnswer.hasOwnProperty(key)) {
            const newKey = `&${key}`;
            fibAnswer[newKey] = newAnswer[key];
          }
        }
      }
      const reqBody = IS_FIB_QUESTION
        ? {
            userSolutions: null,
            userFibSolutions: encodeToBase64(JSON.stringify(fibAnswer))
          }
        : {
            userSolutions: newAnswer,
            userFibSolutions: null
          };

      let callSaveApi = false;

      if (
        (IS_FIB_QUESTION &&
          Object.values(fibAnswer).some((val: any) => val?.trim() !== "")) ||
        (!IS_FIB_QUESTION && newAnswer.length > 0)
      ) {
        callSaveApi = true;
      }
      if (callSaveApi) {
        const res = await attemptQuestion({
          questionId: question.questionId,
          ongoingId: ongoingId,
          body: reqBody
        });

        // Updating timer on save
        if (res?.data?.data?.ongoingQpDuration) {
          dispatch(
            AssessmentSliceActions.mutateTimeLeft(
              res?.data?.data?.ongoingQpDuration
            )
          );
        }
        if (res.error) {
          throw {
            status: "",
            title: "Failed to attempt the question",
            err: res.error,
            description: ""
          };
        }
      }

      if (question.explanation) {
        if (!hasSavedOnce) {
          setHasSavedOnce(true);
          if (isExplanationPresent(question?.explanation))
            setShowExplanation(true);
          setAttemptingQuestion(false);
        }
      }

      if (callSaveApi) {
        dispatch(
          AssessmentSliceActions.mutateQuestionProgress({
            questionId: question.questionId,
            sectionId: sectionId,
            status: "completed"
          })
        );
      }

      if (revealExplanationOnly) {
        if (isExplanationPresent(question?.explanation))
          setShowExplanation(true);
        setHasSavedAnswer(true);
      } else if (isFinalSubmit) {
        if (isLastQuestionInTheSection) {
          dispatch(AssessmentSliceActions.mutateShowSummaryModal(true));
        } else {
          setRevealedHintsCount(0);
          setShowHintSection(false);
          changeActiveQuestion("next");
        }
      }

      setAttemptingQuestion(false);
    } catch (err: any) {
      console.error(err);
      const temp = err;
      const rawErrList = temp?.err?.data?.error;
      const errList =
        rawErrList?.length > 0
          ? rawErrList?.map((el: string) => el.split("_")[0])
          : [];
      if (showErrors)
        showErrors(temp?.title, errList, "error", temp?.description);
      setAttemptingQuestion(false);
    }
  };

  const saveAndNext = async () => {
    setAttemptingQuestion(true);
    try {
      if (!ongoingId) {
        throw {
          status: "",
          title: "Ongoing Id Not Found",
          err: {},
          description: ""
        };
      }

      const newAnswer =
        candidateResponseForQuestion || (IS_FIB_QUESTION ? {} : []);

      const fibAnswer: Record<string, string> = {};
      if (IS_FIB_QUESTION && Object.keys(newAnswer).length > 0) {
        /* We are renaming all keys to replace blank with &blank  in case of FIB  solution*/

        for (const key in newAnswer) {
          if (newAnswer.hasOwnProperty(key)) {
            const newKey = `&${key}`;
            fibAnswer[newKey] = newAnswer[key];
          }
        }
      }

      const reqBody = IS_FIB_QUESTION
        ? {
            userSolutions: null,
            userFibSolutions: encodeToBase64(JSON.stringify(fibAnswer))
          }
        : {
            userSolutions: newAnswer,
            userFibSolutions: null
          };

      let callSaveApi = false;
      if (
        (IS_FIB_QUESTION &&
          Object.values(fibAnswer).some((val: any) => val?.trim() !== "")) ||
        (!IS_FIB_QUESTION && newAnswer.length > 0)
      ) {
        callSaveApi = true;
      }

      if (callSaveApi) {
        const res = await attemptQuestion({
          questionId: question.questionId,
          ongoingId: ongoingId,
          body: reqBody
        });

        // Updating timer on save
        if (res?.data?.data?.ongoingQpDuration) {
          dispatch(
            AssessmentSliceActions.mutateTimeLeft(
              res?.data?.data?.ongoingQpDuration
            )
          );
        }
        if (res.error) {
          throw {
            status: "",
            title: "Failed to attempt the question",
            err: res.error,
            description: ""
          };
        }
      }
      const res2 = await startQuestion({
        actionType: "completed",
        ongoingId: ongoingId,
        questionId: question.questionId
      });

      if (res2.error) {
        setAttemptingQuestion(false);
        throw {
          status: "",
          title: "Failed to complete the question",
          err: res2.error,
          description: ""
        };
      }

      if (callSaveApi) {
        dispatch(
          AssessmentSliceActions.mutateQuestionProgress({
            questionId: question.questionId,
            sectionId: sectionId,
            status: "completed"
          })
        );
      }
      setRevealedHintsCount(0);
      setShowHintSection(false);
      changeActiveQuestion("next");
      setAttemptingQuestion(false);
    } catch (err: any) {
      console.error(err);
      const temp = err;
      const rawErrList = temp?.err?.data?.error;
      const errList =
        rawErrList?.length > 0
          ? rawErrList?.map((el: string) => el.split("_")[0])
          : [];
      if (showErrors)
        showErrors(temp?.title, errList, "error", temp?.description);
      setAttemptingQuestion(false);
    }
  };

  const flagQuestionHandler = async () => {
    try {
      setAttemptingQuestion(true);

      if (!ongoingId) {
        throw {
          status: "",
          title: "Ongoing Id Not Found",
          err: {},
          description: ""
        };
      }

      const res = await flagQuestion({
        flag: questionFlagged ? "false" : "true",
        ongoingId: ongoingId,
        questionId: question.questionId
      });
      if (res.error) {
        throw {
          status: "",
          title: "Error updating the status of the question",
          err: res.error,
          description: ""
        };
      }

      dispatch(
        AssessmentSliceActions.mutateQuestionFlagged({
          questionId: question.questionId,
          sectionId: sectionId,
          value: !questionFlagged
        })
      );
      setAttemptingQuestion(false);
    } catch (err) {
      console.error(err);
      const temp: any = err;
      const rawErrList = temp?.err?.data?.error;
      const errList =
        rawErrList?.length > 0
          ? rawErrList?.map((el: string) => el.split("_")[0])
          : [];
      if (showErrors)
        showErrors(temp?.title, errList, "error", temp?.description);
      setAttemptingQuestion(false);
    }
  };

  const resetQuestionResponseHandler = async () => {
    try {
      if (!activeQuestionMeta?.questionId) return;
      setShowExplanation(false);
      setAttemptingQuestion(true);

      if (!ongoingId) {
        throw {
          status: "",
          title: "Ongoing Id Not Found",
          err: {},
          description: ""
        };
      }
      const reqBody = IS_FIB_QUESTION
        ? { userSolutions: null, userFibSolutions: {} }
        : { userSolutions: [], userFibSolutions: null };

      let callSaveApi = false;
      if (IS_FIB_QUESTION) {
        if (
          candidateResponseForQuestion &&
          Object.keys(candidateResponseForQuestion).length > 0
        )
          callSaveApi = true;
      } else {
        if (
          candidateResponseForQuestion &&
          candidateResponseForQuestion.length > 0
        ) {
          if (currentStatus != "completed") {
            dispatch(
              AssessmentSliceActions.mutateCandidateResponse({
                questionId: activeQuestionMeta?.questionId,
                answerKey: []
              })
            );
          } else callSaveApi = true;
        }
      }

      if (callSaveApi) {
        const res = await attemptQuestion({
          questionId: question.questionId,
          ongoingId: ongoingId,
          body: reqBody
        });

        // Updating timer on save
        if (res?.data?.data?.ongoingQpDuration) {
          dispatch(
            AssessmentSliceActions.mutateTimeLeft(
              res?.data?.data?.ongoingQpDuration
            )
          );
        }

        if (res.error) {
          throw {
            status: "",
            title: "Failed to attempt the question",
            err: res.error,
            description: ""
          };
        }

        const res2 = await startQuestion({
          actionType: "completed",
          ongoingId: ongoingId,
          questionId: question.questionId
        });

        if (res2.error) {
          setAttemptingQuestion(false);
          throw {
            status: "",
            title: "Failed to complete the question",
            err: res2.error,
            description: ""
          };
        }
      }

      if (question.subType === "fib") {
        dispatch(
          AssessmentSliceActions.mutateCandidateFibResponse({
            questionId: activeQuestionMeta?.questionId,
            answerKey: null
          })
        );
        dispatch(
          AssessmentSliceActions.mutateQuestionProgress({
            questionId: question.questionId,
            sectionId: sectionId,
            status: "unanswered"
          })
        );
      } else {
        dispatch(
          AssessmentSliceActions.mutateCandidateResponse({
            questionId: activeQuestionMeta?.questionId,
            answerKey: []
          })
        );
        dispatch(
          AssessmentSliceActions.mutateQuestionProgress({
            questionId: question.questionId,
            sectionId: sectionId,
            status: "unanswered"
          })
        );
      }
      setAttemptingQuestion(false);
    } catch (err) {
      console.error(err);
      const temp: any = err;
      const rawErrList = temp?.err?.data?.error;
      const errList =
        rawErrList?.length > 0
          ? rawErrList?.map((el: string) => el.split("_")[0])
          : [];
      if (showErrors)
        showErrors(temp?.title, errList, "error", temp?.description);
      setAttemptingQuestion(false);
    }
  };

  function handleHintClick() {
    if (isHintLoading || revealedHintsCount >= (question?.hint?.length || 0))
      return;
    setShowHintSection(true);
    setRevealedHintsCount((prev) => prev + 1);
    setIsHintLoading(true);
    setProgress(0);

    let currentProgress = 0;
    const interval = setInterval(() => {
      currentProgress += 2; // 2% every 100ms = 5s
      setProgress(currentProgress);

      if (currentProgress >= 100) {
        clearInterval(interval);
        setIsHintLoading(false);
        setProgress(0);
      }
    }, 100);
  }

  const isExplanationPresent = (explanation: any) => {
    if (explanation) {
      const vals = Object.values(explanation).filter((v) => v != "");
      return vals.length > 0;
    }
    return false;
  };

  useEffect(() => {
    const updateAvailableViewportHeight = () => {
      if (!containerRef.current) return;

      const topOffset = containerRef.current.getBoundingClientRect().top;
      const bottomSpacing = window.innerWidth >= 1024 ? 24 : 16;
      const nextHeight = Math.max(
        window.innerHeight - topOffset - bottomSpacing,
        320
      );

      setAvailableViewportHeight(nextHeight);
    };

    updateAvailableViewportHeight();
    window.addEventListener("resize", updateAvailableViewportHeight);
    window.addEventListener("orientationchange", updateAvailableViewportHeight);

    return () => {
      window.removeEventListener("resize", updateAvailableViewportHeight);
      window.removeEventListener(
        "orientationchange",
        updateAvailableViewportHeight
      );
    };
  }, []);

  return (
    <div
      ref={containerRef}
      style={
        {
          ...(availableViewportHeight
            ? { "--objective-panel-height": `${availableViewportHeight}px` }
            : {})
        } as CSSProperties
      }
      className="w-full flex min-h-0 flex-col xl:pt-20 pt-8 md:pt-10 px-3 sm:px-4 md:px-6 lg:px-8 md:border md:border-secondary/10 dark:border-none dark:bg-[#1E1E1E] md:rounded-2xl pb-3 lg:h-[var(--objective-panel-height)] lg:max-h-[var(--objective-panel-height)] lg:overflow-hidden"
    >
      <div className="flex-1 min-h-0 overflow-y-auto thin-scrollbar">
        <div className="max-w-7xl mx-auto w-full">
          {/* Top */}
          <div className="flex items-centre justify-between">
            <div className="flex flex-wrap items-center gap-3 mb-5 justify-start max-w-full">
              <div>
                <h3 className="text-base font-semibold leading-6 text-primary dark:text-secondary truncate">
                  {t("assessment.objective.questionCounter")}{" "}
                  {question.orderInSection} / {totalQuestionsInSection}{" "}
                </h3>
                {showQuestionTitle && question?.questiontitle && (
                  <div className="text-xl font-bold py-2">
                    {question?.questiontitle}
                  </div>
                )}
              </div>
              {questionFlagged && (
                <GetHeroIcon
                  name="FlagIcon"
                  className="w-5 h-5 text-[#AA6207] flex-shrink-0"
                />
              )}
            </div>
            {preAssessmentData?.questionConfig?.showQuestionMarks && (
              <h3 className="text-base sm:text-lg font-bold leading-5 text-secondary truncate">
                {t("assessment.objective.marks")} {question?.maxMarks}
              </h3>
            )}
          </div>
          {/*Default Question */}
          {question.subType !== "fib" && (
            <div
              dangerouslySetInnerHTML={{ __html: question.problemStatement }}
              className="mb-10 text-xl font-medium"
              style={{
                userSelect: "none",
                WebkitUserSelect: "none",
                MozUserSelect: "none"
              }}
            />
          )}
          {/* Fib Question */}
          {question.subType === "fib" && (
            <ObjectiveFibQuestion
              problemStatement={question.problemStatement}
              answerKey={candidateResponseForQuestion}
              updateAnswerKeyHandler={updateFibAnswerKeyHandler}
              caseSensitive={question.caseSensitive}
            />
          )}
          {/* Options */}
          {question.subType !== "fib" && (
            <ObjectiveOptionsInput
              options={question.options!}
              subtype={question.subType}
              answerKey={candidateResponseForQuestion}
              updateAnswerKeyHandler={updateAnswerKeyHandler}
              questionId={question.questionId}
            />
          )}
          <div className="mt-10">
            {showExplanation &&
              preAssessmentData?.questionConfig?.showExplanation &&
              question?.explanation &&
              Object.keys(question.explanation).length > 0 && (
                <div className="mt-10" id="explanation-section">
                  <p className="font-semibold text-secondary mb-3 leading-6">
                    {t("assessment.objective.explanation")}
                  </p>
                  <ShowExplanation
                    options={IS_FIB_QUESTION ? [] : question.options}
                    explanation={question.explanation}
                    selectedOption={
                      IS_FIB_QUESTION
                        ? Object.keys(candidateResponseForQuestion || {}).map(
                            (k) => "&" + k
                          )
                        : candidateResponseForQuestion || []
                    }
                    subType={IS_FIB_QUESTION ? "fib" : "mcq"}
                  />
                </div>
              )}
          </div>

          {showHintSection &&
            preAssessmentData?.questionConfig?.showHint &&
            question?.hint?.length > 0 && (
              <div className="mt-10" id="hints-section">
                <p className="font-semibold text-secondary mb-3 leading-6">
                  {t("assessment.objective.hints")}
                </p>
                {hints.slice(0, revealedHintsCount).map((hint, index) => (
                  <div
                    key={index}
                    id={`hint-${index}`} // <-- assign id here on each hint
                    className="mt-3 animate-fadeInUp"
                    style={{
                      animationDelay: `${index * 100}ms`,
                      animationFillMode: "both"
                    }}
                  >
                    <ShowHint hints={[hint]} visibleCount={1} />
                  </div>
                ))}
              </div>
            )}
        </div>
      </div>
      {/* Buttons */}
      <div
        className="flex-none flex flex-col gap-3 md:gap-4 py-3 md:py-4 mt-8 md:mt-10
  bg-white dark:bg-[#1E1E1E] z-10 md:border-t md:border-secondary/10"
      >
        {/* Primary Row - Two Logical Groups */}
        <div className="flex flex-wrap md:flex-nowrap items-center justify-between gap-3 w-full">
          <div className="flex items-center gap-3 flex-wrap">
            <Button
              buttonColor="secondary"
              variant="outlined"
              onClick={resetQuestionResponseHandler}
              disable={attemptingQuestion}
              className="shrink-0"
            >
              {t("assessment.objective.reset")}
            </Button>
            <Button
              className="text-[#AA6207] shrink-0"
              variant="outlined"
              buttonColor="secondary"
              onClick={flagQuestionHandler}
              disable={attemptingQuestion}
            >
              {questionFlagged
                ? t("assessment.objective.unflag")
                : t("assessment.objective.flag")}
            </Button>
          </div>

          <div className="flex items-center gap-3 flex-wrap md:justify-end">
            {question.orderInSection > 1 && (
              <Button
                buttonColor="secondary"
                variant="outlined"
                onClick={async () => {
                  await attemptQuestionHandler(false);
                  changeActiveQuestion("previous");
                }}
                disable={attemptingQuestion}
                className="shrink-0"
              >
                {t("assessment.objective.previous")}
              </Button>
            )}

            {!isLastQuestionInTheSection && (
              <Button
                buttonColor="primary"
                variant="contained"
                onClick={() => saveAndNext()}
                disable={attemptingQuestion}
                className="shrink-0"
              >
                {t("assessment.objective.saveAndNext")}
              </Button>
            )}

            {isLastQuestionInTheSection && (
              <Button
                buttonColor="primary"
                variant="contained"
                onClick={() => attemptQuestionHandler(true)}
                disable={attemptingQuestion}
                className="shrink-0"
              >
                {t("assessment.objective.saveAndViewSummary")}
              </Button>
            )}
          </div>
        </div>

        {/* Secondary Row - Hint / Explanation */}
        <div className="flex flex-wrap items-center gap-3">
          {question?.hint &&
            question?.hint.length > 0 &&
            (() => {
              const hints = question.hint;
              const allHintsShown = revealedHintsCount >= hints.length;

              const getTooltipTitle = () => {
                if (isHintLoading)
                  return t("assessment.objective.hintWaitTooltip");
                if (allHintsShown)
                  return t("assessment.objective.noHintsPendingTooltip");
                return "";
              };

              const buttonElement = (
                <Button
                  onClick={handleHintClick}
                  disabled={isHintLoading || allHintsShown}
                  variant="outlined"
                  buttonColor="secondary"
                  className="relative overflow-hidden text-[#FBBF24] shrink-0"
                >
                  {isHintLoading && (
                    <div
                      className="absolute top-0 left-0 h-full bg-secondary-bg dark:bg-secondary/20"
                      style={{ width: `${progress}%` }}
                    />
                  )}

                  <span className="relative z-10 flex items-center gap-2">
                    {t("assessment.subjective.showHint")}
                  </span>
                </Button>
              );

              return getTooltipTitle() ? (
                <Tooltip message={getTooltipTitle()} position="bottom-16">
                  <div>{buttonElement}</div>
                </Tooltip>
              ) : (
                buttonElement
              );
            })()}

          {preAssessmentData?.questionConfig?.showExplanation &&
            isExplanationPresent(question?.explanation) &&
            (() => {
              const tooltipMessage = !hasSelectedAnswer
                ? t("assessment.objective.selectOptionForExplanation")
                : "";

              const buttonElement = (
                <Button
                  buttonColor="secondary"
                  variant="outlined"
                  onClick={handleExplanationClick}
                  disabled={!hasSelectedAnswer}
                  className="shrink-0"
                >
                  {showExplanation
                    ? t("assessment.objective.hideExplanation")
                    : t("assessment.objective.showExplanation")}
                </Button>
              );

              return tooltipMessage ? (
                <Tooltip message={tooltipMessage} position="bottom-16">
                  <div>{buttonElement}</div>
                </Tooltip>
              ) : (
                buttonElement
              );
            })()}
        </div>
      </div>
    </div>
  );
};

export default AssessmentObjectiveQuestions;
